/**
 * UNA SECCIÓN QUE SE PLIEGA: título arriba, el resto adentro.
 *
 * ====================================================================
 * POR QUÉ EXISTE
 * ====================================================================
 *
 * El perfil medía 7.478 píxeles en un teléfono: nueve pantallas seguidas de
 * foto, datos, documento, dirección, emergencia y contraseña, sin una sola
 * división. Para cambiar la contraseña había que pasar por la cédula. Plegado,
 * se ven tres renglones y cada uno dice qué tiene adentro.
 *
 * ====================================================================
 * `<details>` DEL NAVEGADOR, NO UN ACORDEÓN HECHO A MANO
 * ====================================================================
 *
 * Abre y cierra sin JavaScript, el teclado lo maneja solo (Enter y espacio
 * sobre el título), y un lector de pantalla anuncia «contraído» o
 * «expandido» sin que haya que escribirle un solo `aria-*`. Un acordeón
 * propio tendría que reinventar las tres cosas, y siempre se olvida alguna.
 *
 * EL TÍTULO ES UN `<h2>` DE VERDAD, adentro del `<summary>` — el estándar lo
 * permite. Así quien navega por títulos sigue encontrando «Tus datos» y
 * «Contraseña» como antes.
 *
 * ====================================================================
 * CERRADOS AL ENTRAR, SALVO QUE HAYA ALGO QUE HACER
 * ====================================================================
 *
 * `abierta` decide solo cómo arranca. Después manda la persona: si la abre,
 * queda abierta aunque lo que la hizo abrirse deje de valer. Se usa para la
 * cuenta vieja sin categoría: tiene que ver el aviso sin buscarlo.
 *
 * Y UN ERROR NUNCA QUEDA ESCONDIDO: los errores salen al apretar el botón de
 * guardar, y ese botón está adentro de la sección. Si se ve el error, la
 * sección ya estaba abierta.
 */
import { useState, type ReactNode } from 'react';

interface Props {
  titulo: string;
  /** Una línea que dice qué hay adentro, para decidir sin abrir. */
  resumen: string;
  /** Solo cómo arranca. Ver arriba. */
  abierta?: boolean;
  children: ReactNode;
}

export function Plegable({ titulo, resumen, abierta = false, children }: Props): JSX.Element {
  const [open, setOpen] = useState(abierta);

  return (
    <details
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      /*
        UNA LÍNEA ARRIBA Y NO UNA CAJA ALREDEDOR. Con caja, el borde y su
        relleno le comían 34 px de ancho a lo de adentro, y «Masculino» y
        «Femenino» dejaban de caber lado a lado y se apilaban. Con líneas, el
        contenido tiene el mismo ancho que antes de plegarlo. La última de la
        tanda cierra con una línea abajo.
      */
      className="group border-t border-border-subtle last-of-type:border-b"
    >
      {/* `list-none` y el marcador de WebKit escondido: el triángulo de
          fábrica sale a la izquierda y descentrado. Va uno propio abajo,
          que gira al abrir. */}
      <summary className="flex cursor-pointer list-none flex-col items-center gap-0.5 py-4 [&::-webkit-details-marker]:hidden">
        <h2 className="m-0 text-lg">{titulo}</h2>
        <span className="text-sm text-muted">{resumen}</span>
        <span aria-hidden="true" className="mt-1 text-xs leading-none text-muted transition-transform group-open:rotate-180">
          ▾
        </span>
      </summary>

      {/*
        LAS AYUDAS, MÁS CHICAS. Casi cada campo trae una explicación en gris
        del mismo tamaño que el resto, y juntas duplicaban el largo de la
        pantalla y dejaban de leerse. Se achican ACÁ y no en `.field .help`:
        esa regla la usan once pantallas, y esto es el piloto.
      */}
      <div className="pt-1 pb-6 [&_.help]:mb-1 [&_.help]:text-xs">
        {children}
      </div>
    </details>
  );
}
